/**
 * DV-DEP-001: Upload to YouTube Modal Component
 *
 * Modal component for uploading a production to YouTube immediately
 * with title, description, tags, and visibility settings.
 */

import React, { useState, useEffect } from 'react';
import { X, Upload, AlertCircle, Youtube, Tag, Eye, Clock } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useCreateYoutubeUpload, useYoutubeIntegration } from '@/hooks/useDeployments';
import { DeploymentsAPIService } from '@/services/deployments-api';
import type { Production, YoutubeUploadFormData } from '@/types/deployments';

interface UploadToYouTubeModalProps {
  production: Production;
  onClose: () => void;
}

const UploadToYouTubeModal: React.FC<UploadToYouTubeModalProps> = ({
  production,
  onClose,
}) => {
  const [formData, setFormData] = useState<YoutubeUploadFormData>({
    title: production.title || '',
    description: production.description || '',
    tags: [],
    visibility: 'unlisted',
    scheduleType: 'now',
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const createUpload = useCreateYoutubeUpload();
  const { data: integrationData } = useYoutubeIntegration();

  const isConnected = integrationData?.integration?.isConnected ?? false;

  useEffect(() => {
    // Auto-generate title if empty
    if (!formData.title) {
      setFormData(prev => ({
        ...prev,
        title: production.title || `HI-SI Production ${production.id}`,
      }));
    }
  }, [production.title, production.id, formData.title]);

  const handleInputChange = (field: keyof YoutubeUploadFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors([]); // Clear errors on input change
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 15) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form data
    const validationErrors = DeploymentsAPIService.validateYoutubeUploadData({
      title: formData.title,
      description: formData.description,
      tags: formData.tags,
      visibility: formData.visibility,
    });

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await createUpload.mutateAsync({
        productionId: production.id,
        data: {
          title: formData.title,
          description: formData.description,
          tags: formData.tags,
          visibility: formData.visibility,
        },
      });

      onClose();
    } catch (error: any) {
      // Extract server error message if available
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.message ||
                          'Failed to start upload. Please try again.';
      setErrors([errorMessage]);
    }
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  const getVisibilityDescription = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return 'Anyone can search for and view';
      case 'unlisted':
        return 'Anyone with the link can view';
      case 'private':
        return 'Only you can view';
      default:
        return '';
    }
  };

  if (!isConnected) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-black border border-red-500/30 rounded-lg p-6 max-w-md w-full">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-medium text-white">YouTube Not Connected</h2>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-white/80 mb-2">
              You need to connect your YouTube account first
            </p>
            <p className="text-white/60 text-sm">
              Please connect your YouTube account in the integration card above to upload videos.
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-black border border-cyan-500/30 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center justify-center">
              <Youtube className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-medium text-white">Upload to YouTube</h2>
              <p className="text-white/60 text-sm">Upload now and publish immediately</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Production Info */}
        <div className="p-6 border-b border-white/10 bg-white/5">
          <div className="flex items-start space-x-4">
            {production.thumbnailUrl && (
              <img
                src={production.thumbnailUrl}
                alt={production.title}
                className="w-24 h-16 object-cover rounded border border-white/20"
              />
            )}
            <div className="flex-1">
              <h3 className="text-white font-medium mb-1">
                {production.title || `Production ${production.id}`}
              </h3>
              <div className="flex items-center space-x-4 text-sm text-white/60 font-mono">
                <span>{production.aspectRatio}</span>
                {production.duration && (
                  <span className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDuration(production.duration)}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-white/80 font-medium mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              maxLength={100}
              className="w-full px-4 py-3 bg-black/60 border border-cyan-500/20 rounded-lg text-white placeholder-white/40 focus:border-cyan-500/40 focus:outline-none"
              placeholder="Enter video title..."
              required
            />
            <div className="flex justify-between mt-1">
              <p className="text-white/40 text-xs">Required for YouTube upload</p>
              <p className="text-white/40 text-xs">{formData.title.length}/100</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-white/80 font-medium mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              maxLength={5000}
              rows={4}
              className="w-full px-4 py-3 bg-black/60 border border-cyan-500/20 rounded-lg text-white placeholder-white/40 focus:border-cyan-500/40 focus:outline-none resize-none"
              placeholder="Enter video description..."
            />
            <div className="flex justify-between mt-1">
              <p className="text-white/40 text-xs">Optional description for your video</p>
              <p className="text-white/40 text-xs">{formData.description.length}/5000</p>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-white/80 font-medium mb-2">
              Tags ({formData.tags.length}/15)
            </label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 px-4 py-2 bg-black/60 border border-cyan-500/20 rounded-lg text-white placeholder-white/40 focus:border-cyan-500/40 focus:outline-none"
                placeholder="Add a tag..."
                disabled={formData.tags.length >= 15}
              />
              <button
                type="button"
                onClick={handleAddTag}
                disabled={!tagInput.trim() || formData.tags.length >= 15}
                className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Tag className="w-4 h-4" />
              </button>
            </div>

            {/* Tags Display */}
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center space-x-1 px-2 py-1 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded text-sm"
                  >
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-cyan-400/60 hover:text-cyan-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-white/80 font-medium mb-2">
              Visibility
            </label>
            <div className="space-y-2">
              {(['public', 'unlisted', 'private'] as const).map((visibility) => (
                <label
                  key={visibility}
                  className="flex items-start space-x-3 p-3 bg-black/40 border border-white/10 rounded-lg hover:border-white/20 transition-colors cursor-pointer"
                >
                  <input
                    type="radio"
                    name="visibility"
                    value={visibility}
                    checked={formData.visibility === visibility}
                    onChange={(e) => handleInputChange('visibility', e.target.value)}
                    className="mt-1 text-cyan-500 focus:ring-cyan-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-4 h-4 text-white/60" />
                      <span className="text-white font-medium capitalize">{visibility}</span>
                    </div>
                    <p className="text-white/60 text-sm mt-1">
                      {getVisibilityDescription(visibility)}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-red-400 font-medium mb-1">Please fix the following errors:</h4>
                  <ul className="space-y-1">
                    {errors.map((error, index) => (
                      <li key={index} className="text-red-300 text-sm">
                        • {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-black/60 border border-white/20 text-white/80 rounded-lg hover:bg-black/80 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createUpload.isPending || !formData.title.trim()}
              className={cn(
                'px-6 py-2 border rounded-lg font-medium transition-colors',
                'flex items-center space-x-2',
                createUpload.isPending || !formData.title.trim()
                  ? 'bg-gray-500/20 border-gray-500/30 text-gray-400 cursor-not-allowed'
                  : 'bg-green-500/20 border-green-500/30 text-green-400 hover:bg-green-500/30'
              )}
            >
              <Upload className="w-4 h-4" />
              <span>
                {createUpload.isPending ? 'Starting Upload...' : 'Upload Now'}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadToYouTubeModal;